import React from "react";
import RiskScatterChart from "@/components/graph/RiskScatterChart";
import {
    getSectionRiskColorKey,
    SECTION_RISK_COLOR_PALETTE,
} from "@/services/sectionRiskService";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/Card"
import { Label, PolarAngleAxis, PolarGrid, PolarRadiusAxis, RadialBar, RadialBarChart } from "recharts"
import {
    ChartContainer,
} from "@/components/ui/chart"



const chartConfig = {
    score: {
        label: "Inherent Risk",
    },
    inherentRisk: {
        label: "Inherent Risk",
        color: "#f6c637",
    },
}

const OverallMineScoreTab = ({ sectionRiskRows: sectionRiskSourceRows = [] }) => {
    const radialSectionRef = React.useRef(null)
    const [isRadialSectionVisible, setIsRadialSectionVisible] = React.useState(false)
    const [animatedScores, setAnimatedScores] = React.useState([])

    const colorPalette = SECTION_RISK_COLOR_PALETTE

    const getTextColorClass = (colorKey) => {
        const whiteTextColors = ["red", "green", "blue"]
        return whiteTextColors.includes(colorKey) ? "text-gray-0" : "text-gray-900"
    }

    const getMatrixCellColorKey = (value) => getSectionRiskColorKey(value) ?? "blue"

    const riskCategories = [
        { label: "Severe", span: 2 },
        { label: "Major", span: 2 },
        { label: "Moderate", span: 2 },
        { label: "Minor", span: 2 },
        { label: "Insignificant", span: 2 },
    ]

    const controlEfficacyRows = [
        { label: "Excellent", score: 10, values: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100] },
        { label: "", score: 9, values: [9, 18, 27, 36, 45, 54, 63, 72, 81, 90] },
        { label: "Good", score: 8, values: [8, 16, 24, 32, 40, 48, 56, 64, 72, 80] },
        { label: "", score: 7, values: [7, 14, 21, 28, 35, 42, 49, 56, 63, 70] },
        { label: "Adequate", score: 6, values: [6, 12, 18, 24, 30, 36, 42, 48, 54, 60] },
        { label: "", score: 5, values: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50] },
        { label: "Limited", score: 4, values: [4, 8, 12, 16, 20, 24, 28, 32, 36, 40] },
        { label: "", score: 3, values: [3, 6, 9, 12, 15, 18, 21, 24, 27, 30] },
        { label: "Poor", score: 2, values: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20] },
        { label: "", score: 1, values: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
    ]

    const graphDataRows = React.useMemo(() => {
        if (!Array.isArray(sectionRiskSourceRows)) return []

        const groups = new Map()

        sectionRiskSourceRows.forEach((row) => {
            const riskName = (row.risk || "").trim()
            if (!riskName) return
            if (!groups.has(riskName)) {
                groups.set(riskName, [])
            }
            groups.get(riskName).push(row)
        })

        const parseValidNumber = (value) => {
            if (value === null || value === undefined) return null
            const stringValue = String(value).trim()
            if (stringValue === "") return null
            const numeric = Number(stringValue)
            if (Number.isNaN(numeric)) return null
            return numeric
        }

        const average = (values) => {
            if (!values.length) return ""
            const sum = values.reduce((acc, v) => acc + v, 0)
            const avg = sum / values.length
            return Number.isFinite(avg) ? avg.toFixed(1) : ""
        }

        const result = []

        groups.forEach((rows, riskName) => {
            const inherentValues = []
            const controlValues = []
            const riskScoreValues = []

            rows.forEach((row) => {
                const inherent = parseValidNumber(row.inherentRiskScore)
                if (inherent !== null) inherentValues.push(inherent)

                const control = parseValidNumber(row.controlEfficacy)
                if (control !== null) controlValues.push(control)

                const riskScore = parseValidNumber(row.riskScore)
                if (riskScore !== null) riskScoreValues.push(riskScore)
            })

            result.push({
                risk: riskName,
                inherent: average(inherentValues),
                efficacy: average(controlValues),
                percent: average(riskScoreValues),
            })
        })

        return result
    }, [sectionRiskSourceRows])

    const mineOverallRow = React.useMemo(() => {
        if (!graphDataRows.length) return null

        const parseValidNumber = (value) => {
            if (value === null || value === undefined) return null
            const stringValue = String(value).trim()
            if (stringValue === "") return null
            const numeric = Number(stringValue)
            if (Number.isNaN(numeric)) return null
            return numeric
        }

        const average = (values) => {
            if (!values.length) return ""
            const sum = values.reduce((acc, v) => acc + v, 0)
            const avg = sum / values.length
            return Number.isFinite(avg) ? avg.toFixed(1) : ""
        }

        const inherentValues = []
        const efficacyValues = []
        const percentValues = []

        graphDataRows.forEach((row) => {
            const inherent = parseValidNumber(row.inherent)
            if (inherent !== null) inherentValues.push(inherent)

            const efficacy = parseValidNumber(row.efficacy)
            if (efficacy !== null) efficacyValues.push(efficacy)

            const percent = parseValidNumber(row.percent)
            if (percent !== null) percentValues.push(percent)
        })

        return {
            risk: "Mine Overall",
            inherent: average(inherentValues),
            efficacy: average(efficacyValues),
            percent: average(percentValues),
        }
    }, [graphDataRows])

    const getRiskBandMeta = React.useCallback((score, type, hasValidInherent = true) => {
        const numeric = Number(score)
        if (!Number.isFinite(numeric)) {
            return { label: "No Score", color: "#273343", score: 0 }
        }

        const normalizedScore = Math.max(0, Math.min(100, numeric))
        if (normalizedScore <= 0) {
            return { label: "No Score", color: "#273343", score: normalizedScore }
        }

        if (type === "control") {
            if (normalizedScore <= 20) return { label: "Poor", color: "#DA0000", score: normalizedScore };
            if (normalizedScore <= 40) return { label: "Limited", color: "#F66400", score: normalizedScore };
            if (normalizedScore <= 60) return { label: "Adequate", color: "#FEF800", score: normalizedScore };
            if (normalizedScore <= 80) return { label: "Good", color: "#008000", score: normalizedScore };
            return { label: "Excellent", color: "#0000FF", score: normalizedScore };
        }

        if (!hasValidInherent) {
            if (normalizedScore <= 20) return { label: "Severe", color: "#DA0000", score: normalizedScore };
            if (normalizedScore <= 40) return { label: "Major", color: "#F66400", score: normalizedScore };
            if (normalizedScore <= 60) return { label: "Moderate", color: "#FEF800", score: normalizedScore };
            if (normalizedScore <= 80) return { label: "Minor", color: "#008000", score: normalizedScore };
            return { label: "Insignificant", color: "#0000FF", score: normalizedScore };
        }

        const ranges = [
            { maxExclusive: 6.1, color: "#DA0000", labels: { risk: "Severe", overall: "Severe" } },
            { maxExclusive: 20.1, color: "#F66400", labels: { risk: "Major", overall: "Major" } },
            { maxExclusive: 40.1, color: "#FEF800", labels: { risk: "Moderate", overall: "Moderate" } },
            { maxExclusive: 79.1, color: "#008000", labels: { risk: "Minor", overall: "Minor" } },
            { maxExclusive: 101, color: "#0000FF", labels: { risk: "Insignificant", overall: "Insignificant" } },
        ]

        const matchedRange = ranges.find((range) => normalizedScore < range.maxExclusive)
        if (!matchedRange) {
            return { label: "No Score", color: "#273343", score: normalizedScore }
        }

        return {
            label: type === "overall" ? matchedRange.labels.overall : matchedRange.labels.risk,
            color: matchedRange.color,
            score: normalizedScore,
        }
    }, [])

    const radialCharts = React.useMemo(() => {
        const parsePercent = (value) => {
            const numeric = Number(value)
            if (!Number.isFinite(numeric)) return 0
            return Math.max(0, Math.min(100, numeric))
        }

        const inherentPercent = parsePercent(Number(mineOverallRow?.inherent) * 10)
        const controlPercent = parsePercent(Number(mineOverallRow?.efficacy) * 10)
        const overallPercent = parsePercent(mineOverallRow?.percent)

        const hasValidInherent = Number(mineOverallRow?.inherent) > 0;

        const inherentMeta = getRiskBandMeta(inherentPercent, "risk", hasValidInherent)
        const controlMeta = getRiskBandMeta(controlPercent, "control", hasValidInherent)
        const overallMeta = getRiskBandMeta(overallPercent, "overall", hasValidInherent)

        return [
            { title: "Inherent Risk", ...inherentMeta },
            { title: "Control Efficacy", ...controlMeta },
            { title: "Overall Risk", ...overallMeta },
        ]
    }, [getRiskBandMeta, mineOverallRow])

    React.useEffect(() => {
        const element = radialSectionRef.current
        if (!element) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsRadialSectionVisible(entry.isIntersecting)
            },
            { threshold: 0.35 }
        )

        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    React.useEffect(() => {
        if (!isRadialSectionVisible) return

        setAnimatedScores(radialCharts.map(() => 0))
        const animationFrame = window.requestAnimationFrame(() => {
            setAnimatedScores(radialCharts.map((chart) => chart.score))
        })

        return () => window.cancelAnimationFrame(animationFrame)
    }, [isRadialSectionVisible, radialCharts])

    const legendBands = [
        { score: "1 to 6", color: colorPalette.red, inherentRisk: "Severe", controlEfficacy: "Poor" },
        { score: "> 6 to 20", color: colorPalette.orange, inherentRisk: "Major", controlEfficacy: "Limited" },
        { score: "> 20 to 40", color: colorPalette.yellow, inherentRisk: "Moderate", controlEfficacy: "Adequate" },
        { score: "> 40 to 79", color: colorPalette.green, inherentRisk: "Minor", controlEfficacy: "Good" },
        { score: "> 79 to 100", color: colorPalette.blue, inherentRisk: "Insignificant", controlEfficacy: "Excellent" },
    ]

    const pdBiLikelihoodBands = [
        {
            color: colorPalette.red,
            propertyDamage: ">USD50.0M",
            businessInterruption: ">12 months",
            likelihood: "Certain",
        },
        {
            color: colorPalette.orange,
            propertyDamage: ">USD10.0M - 50.0M",
            businessInterruption: ">180 to 12 months",
            likelihood: "Likely",
        },
        {
            color: colorPalette.yellow,
            propertyDamage: ">USD5.0M - 10.0M",
            businessInterruption: ">90 to 180 Days",
            likelihood: "Possible",
        },
        {
            color: colorPalette.green,
            propertyDamage: ">USD500k - 5.0M",
            businessInterruption: ">30 to 90 Days",
            likelihood: "Unlikely",
        },
        {
            color: colorPalette.blue,
            propertyDamage: "<USD500k",
            businessInterruption: "</=30 Days",
            likelihood: "Rare",
        },
    ]

    const riskDataBands = [
        { score: "10 or 20", color: colorPalette.red, overallRisk: "Severe" },
        { score: "30 or 40", color: colorPalette.orange, overallRisk: "Major" },
        { score: "50 or 60", color: colorPalette.yellow, overallRisk: "Moderate" },
        { score: "70 or 80", color: colorPalette.green, overallRisk: "Minor" },
        { score: "90 or 100", color: colorPalette.blue, overallRisk: "Insignificant" },
    ]

    const legendLabelCellClass =
        "border border-gray-600 bg-navy-500 px-3 py-2.5 text-left text-sm font-semibold uppercase text-gray-0 align-middle"

    const legendDataCellClass =
        "border border-gray-600 bg-navy-500 px-3 py-2.5 text-center text-sm font-semibold text-gray-0 align-middle"

    return (
        <div className="py-4 space-y-6 slide-up">
            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <h5 className="text-lg font-semibold uppercase text-gray-0">Legend</h5>
                    <h3 className="text-sm font-semibold text-gray-0 uppercase">Final Score</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] table-fixed border-collapse">
                            <colgroup>
                                <col className="w-[24%]" />
                                <col span={5} />
                            </colgroup>
                            <tbody>
                                <tr>
                                    <td className={legendLabelCellClass}>Final Score</td>
                                    {legendBands.map((band) => (
                                        <td key={`score-${band.score}`} className={legendDataCellClass}>
                                            {band.score}
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className={legendLabelCellClass}>Colour</td>
                                    {legendBands.map((band) => (
                                        <td
                                            key={`color-${band.score}`}
                                            className="h-11 border border-gray-600 p-0 align-middle"
                                            style={{ backgroundColor: band.color }}
                                        />
                                    ))}
                                </tr>
                                <tr>
                                    <td className={legendLabelCellClass}>Inherent Risk</td>
                                    {legendBands.map((band) => (
                                        <td key={`risk-${band.score}`} className={legendDataCellClass}>
                                            {band.inherentRisk}
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className={legendLabelCellClass}>Control Efficacy</td>
                                    {legendBands.map((band) => (
                                        <td key={`control-${band.score}`} className={legendDataCellClass}>
                                            {band.controlEfficacy}
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h3 className="text-sm font-semibold text-gray-0 uppercase">
                        PD / BI / Likelihood</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] table-fixed border-collapse">
                            <colgroup>
                                <col className="w-[24%]" />
                                <col span={5} />
                            </colgroup>
                            <tbody>
                                <tr>
                                    <td className={legendLabelCellClass}>Colour</td>
                                    {pdBiLikelihoodBands.map((band) => (
                                        <td
                                            key={`pd-color-${band.likelihood}`}
                                            className="h-11 border border-gray-600 p-0 align-middle"
                                            style={{ backgroundColor: band.color }}
                                        />
                                    ))}
                                </tr>
                                <tr>
                                    <td className={legendLabelCellClass}>Property Damage (USD)</td>
                                    {pdBiLikelihoodBands.map((band) => (
                                        <td key={`pd-pd-${band.likelihood}`} className={legendDataCellClass}>
                                            {band.propertyDamage}
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className={legendLabelCellClass}>Business Interruption</td>
                                    {pdBiLikelihoodBands.map((band) => (
                                        <td key={`pd-bi-${band.likelihood}`} className={legendDataCellClass}>
                                            {band.businessInterruption}
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className={legendLabelCellClass}>Likelihood</td>
                                    {pdBiLikelihoodBands.map((band) => (
                                        <td key={`pd-likelihood-${band.likelihood}`} className={legendDataCellClass}>
                                            {band.likelihood}
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <h5 className="text-lg font-semibold uppercase text-gray-0">Overall Risk Scores</h5>
                    <h3 className="text-sm font-semibold text-gray-0 uppercase">Score with control efficacy and inherent risk data</h3>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0" colSpan={2}>
                                        Control Efficacy
                                    </th>
                                    {riskCategories.map((category) => (
                                        <th
                                            key={category.label}
                                            className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0"
                                            colSpan={category.span}
                                        >
                                            Inherent Risk <span className="block text-xs normal-case">{category.label}</span>
                                        </th>
                                    ))}
                                </tr>
                                <tr>
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Category
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Score
                                    </th>
                                    {[...Array(10)].map((_, index) => (
                                        <th
                                            key={index}
                                            className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0"
                                        >
                                            {index + 1}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {controlEfficacyRows.map((row) => (
                                    <tr key={`${row.score}-${row.label || "no-label"}`}>
                                        <td className="px-3 py-2 font-medium text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                            {row.label}
                                        </td>
                                        <td className="px-3 py-2 font-semibold text-center border border-gray-600 bg-navy-500 text-gray-0">
                                            {row.score}
                                        </td>
                                        {row.values.map((value, index) => {
                                            const colorKey = getMatrixCellColorKey(value)
                                            return (
                                                <td
                                                    key={`${row.score}-${index}`}
                                                    className={`px-3 py-2 text-center border border-gray-600 font-semibold ${getTextColorClass(colorKey)}`}
                                                    style={{ backgroundColor: colorPalette[colorKey] }}
                                                >
                                                    {value}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-0 uppercase">Score with only Control Efficacy data</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] table-fixed border-collapse">
                            <colgroup>
                                <col className="w-[24%]" />
                                <col span={5} />
                            </colgroup>
                            <tbody>
                                <tr>
                                    <td className={legendLabelCellClass}>Score</td>
                                    {riskDataBands.map((band) => (
                                        <td key={`riskdata-score-${band.score}`} className={legendDataCellClass}>
                                            {band.score}
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className={legendLabelCellClass}>Colour</td>
                                    {riskDataBands.map((band) => (
                                        <td
                                            key={`riskdata-color-${band.score}`}
                                            className="h-11 border border-gray-600 p-0 align-middle"
                                            style={{ backgroundColor: band.color }}
                                        />
                                    ))}
                                </tr>
                                <tr>
                                    <td className={legendLabelCellClass}>Overall Risk</td>
                                    {riskDataBands.map((band) => (
                                        <td key={`riskdata-risk-${band.score}`} className={legendDataCellClass}>
                                            {band.overallRisk}
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6">
                    <div className="p-4 overflow-x-auto text-gray-900 bg-yellow-300 border border-gray-600 rounded">
                        <div className="text-base font-bold uppercase">
                            Graph Data - Automatic (From section risk scores)
                        </div>
                        <table className="w-full mt-3 text-sm leading-6">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="pb-1 font-semibold text-left uppercase">Inherent Risk</th>
                                    <th className="pb-1 font-semibold text-left uppercase">Control Efficacy</th>
                                    <th className="pb-1 font-semibold text-left uppercase">Risk Section</th>
                                    <th className="pb-1 font-semibold text-right uppercase">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {graphDataRows.map((row) => (
                                    <tr key={row.risk} className="border-b border-gray-500 last:border-b-0">
                                        <td className="py-1">{row.inherent}</td>
                                        <td className="py-1">{row.efficacy}</td>
                                        <td className="py-1">{row.risk}</td>
                                        <td className="py-1 font-semibold text-right">{row.percent}</td>
                                    </tr>
                                ))}
                                {mineOverallRow && (
                                    <tr className="border-t border-gray-700">
                                        <td className="py-1 font-bold">{mineOverallRow.inherent}</td>
                                        <td className="py-1 font-bold">{mineOverallRow.efficacy}</td>
                                        <td className="py-1 font-bold">Overall Risk</td>
                                        <td className="py-1 font-bold text-right">{mineOverallRow.percent}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card ref={radialSectionRef} className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <h5 className="text-lg font-semibold text-center uppercase text-gray-0">Mine Risk Scores</h5>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        {radialCharts.map((item, index) => (
                            <Card key={item.title} className="border-none rounded-md shadow-none bg-navy-200">
                                <CardContent className="flex-1 pb-0">
                                    <ChartContainer
                                        config={chartConfig}
                                        className="mx-auto aspect-square max-h-[230px]"
                                    >
                                        <RadialBarChart
                                            data={[{ risk: item.title, score: animatedScores[index] ?? 0, fill: item.color }]}
                                            startAngle={90}
                                            endAngle={-270}
                                            outerRadius={102}
                                            innerRadius={70}
                                        >
                                            <PolarGrid radialLines={false} stroke="none" />
                                            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} reversed />
                                            <RadialBar
                                                dataKey="score"
                                                background={{ fill: "#273343" }}
                                                cornerRadius={999}
                                                clockWise={false}
                                            />
                                            <PolarRadiusAxis domain={[0, 100]} tick={false} tickLine={false} axisLine={false}>
                                                <Label
                                                    content={({ viewBox }) => {
                                                        if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                                            return (
                                                                <text
                                                                    x={viewBox.cx}
                                                                    y={viewBox.cy}
                                                                    textAnchor="middle"
                                                                    dominantBaseline="middle"
                                                                >
                                                                    <tspan
                                                                        x={viewBox.cx}
                                                                        y={(viewBox.cy || 0) - 6}
                                                                        fill="#ffffff"
                                                                        className="text-xl font-semibold"
                                                                    >
                                                                        {item.label}
                                                                    </tspan>
                                                                    <tspan
                                                                        x={viewBox.cx}
                                                                        y={(viewBox.cy || 0) + 20}
                                                                        fill="#ffffff"
                                                                        className="text-2xl font-medium"
                                                                    >
                                                                        {`${Math.round(item.score)}%`}
                                                                    </tspan>
                                                                </text>
                                                            )
                                                        }
                                                    }}
                                                />
                                            </PolarRadiusAxis>
                                        </RadialBarChart>
                                    </ChartContainer>
                                </CardContent>
                                <CardFooter className="flex-col gap-2 text-sm">
                                    <p className="text-gray-0 text-base font-semibold">{item.title}</p>
                                </CardFooter>
                            </Card>
                        ))}
                    </div>
                    {/* <div className="w-full overflow-x-auto">
                        <div className="min-w-[1024px]">
                            <RiskScatterChart
                                graphDataRows={graphDataRows}
                                mineOverallRow={mineOverallRow}
                            />
                        </div>
                    </div> */}
                </CardContent>
            </Card>
        </div>
    )
}

export default OverallMineScoreTab;
